/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { FactCheckOutlined as FactCheckOutlinedIcon } from 'components/icons';
import MatterIndicators from 'components/matters/MatterIndicators';
import CoiCheckAddRelationButton from 'components/modals/coi/CoiCheckAddRelationsModal';
import CoiCheckEmailSendButton from 'components/modals/coi/CoiCheckEmailSendModal';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Checkbox } from 'components/ui/checkbox';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { getDeletableLinks } from 'pages/matters/util';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useDeleteManuallySelectedCoiCheckRelationMutation,
    useLazyGetCoiChecksCountQuery,
    useLazyGetCoiChecksWithFiltersQuery,
} from 'store/api/coi-check';
import { CoiCheckEmailData, CoiCheckListItem, CoiCheckRelation } from 'types/coi-check';
import { t } from 'utils/getTranslations';
import { createTabState } from 'utils/tabStateUtil';
import { ENTITY_LINK_CLASS } from '../util/CoiCheckUtil';

interface Props {
    handleCoiDetailsClick?: () => void;
}

const PreviousCoiChecksView: React.FC<Props> = ({ handleCoiDetailsClick }) => {
    const [fetchData, { data, isFetching }] = useLazyGetCoiChecksWithFiltersQuery();
    const [deleteCoiCheckRelation] = useDeleteManuallySelectedCoiCheckRelationMutation();
    const [selectedCoiChecks, setSelectedCoiChecks] = useState<CoiCheckEmailData[]>([]);
    const effectiveFormat = useEffectiveDateFormat();

    const handleSearchTermClick = useCallback(() => {
        if (handleCoiDetailsClick) {
            handleCoiDetailsClick();
        }
    }, [handleCoiDetailsClick]);

    const handleCoiCheckSelect = (id: number, searchTerm: string, checkedAt: Date, selected: boolean) => {
        setSelectedCoiChecks((previousValue) => {
            const emailData = { id, searchTerm, checkedAt };
            const copyOfSelectedCoiChecks = [...previousValue];
            if (selected) {
                if (id) copyOfSelectedCoiChecks.push(emailData);
            } else if (id && copyOfSelectedCoiChecks.find((item) => item.id === id)) {
                return copyOfSelectedCoiChecks.filter((item) => item.id !== id);
            }
            return copyOfSelectedCoiChecks;
        });
    };

    const cleanSelections = () => {
        setSelectedCoiChecks([]);
    };

    const coiCheckActionButtons = useCallback(() => {
        return (
            <div className="flex justify-end gap-2">
                <CoiCheckEmailSendButton
                    className="w-[90px]"
                    variant="contained"
                    color="secondary"
                    size="extraSmall"
                    coiCheckEmailData={selectedCoiChecks}
                    cleanSelections={cleanSelections}
                    disabled={selectedCoiChecks.length < 1}
                >
                    <FormattedMessage id="coi.send-email.title" />
                </CoiCheckEmailSendButton>
            </div>
        );
    }, [selectedCoiChecks]);

    const handleDeleteCoiCheckRelation = useCallback(
        (coiCheckId: number, relationId: number | undefined) => {
            if (relationId) {
                deleteCoiCheckRelation({ coiCheckId, relationId });
            }
        },
        [deleteCoiCheckRelation]
    );

    const columns = useMemo<DataTableColumnDef<CoiCheckListItem>[]>(
        () => [
            {
                header: t('coi.search'),
                accessorFn: (row) => row,
                enableSorting: false,
                meta: {
                    widthWeight: 1.72,
                    minPx: 180,
                },
                cell: ({ getValue }) => {
                    const { searchTerm, id, checkedAt } = getValue<CoiCheckListItem>();
                    return (
                        <div className="flex items-center justify-start gap-2">
                            <Checkbox
                                variant="secondary"
                                className="ml-2.5"
                                aria-label={searchTerm}
                                onCheckedChange={(checked) => {
                                    handleCoiCheckSelect(id, searchTerm, checkedAt, checked);
                                }}
                                checked={id ? selectedCoiChecks.find((item) => item.id === id) !== undefined : false}
                            />

                            {id ? (
                                <LinkWithOrigin
                                    className={ENTITY_LINK_CLASS}
                                    to={`/coi-check/${id}`}
                                    onClick={handleSearchTermClick}
                                    state={createTabState('coi-check-page', 'coi-check-details')}
                                >
                                    {searchTerm}
                                </LinkWithOrigin>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('coi.performed-by'),
                accessorFn: (row) => row.checkedByEmployee,
                meta: {
                    widthWeight: 1.59,
                    // Floors sit near the cells' measured min-content widths: both are nowrap, so a
                    // narrower column cuts the name / timestamp rather than wrapping it.
                    minPx: 260,
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckListItem['checkedByEmployee']>();
                    return value ? <div className="whitespace-nowrap">{value.name}</div> : null;
                },
                enableSorting: false,
            },
            {
                header: t('coi.time'),
                accessorFn: (row) => row.checkedAt,
                meta: {
                    widthWeight: 1,
                    minPx: 180,
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckListItem['checkedAt']>();
                    return value ? (
                        <div className="whitespace-nowrap">
                            {format(new Date(value), `${effectiveFormat} HH:mm:ss`)}
                        </div>
                    ) : null;
                },
                enableSorting: false,
            },
            {
                header: t('coi.client-case'),
                accessorFn: (row) => row,
                enableSorting: false,
                meta: {
                    widthWeight: 4.31,
                    minPx: 280,
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckListItem>();
                    const { id: coiCheckId } = value;
                    const connectedMatters = (value.relations || []).filter(
                        (relation: CoiCheckRelation) =>
                            relation.coiCheckScopeTypeCode === 'MATTER' && relation.isManuallySelected
                    );
                    const connectedClients = (value.relations || []).filter(
                        (relation: CoiCheckRelation) =>
                            relation.coiCheckScopeTypeCode === 'CLIENT' && relation.isManuallySelected
                    );

                    const matterLinks = getDeletableLinks(
                        'matter',
                        connectedMatters.map((relation: CoiCheckRelation) => ({
                            id: relation.entityId,
                            name: `${relation.detailsJson.number} ${relation.detailsJson.name}`,
                            prefix: <MatterIndicators isConfidential={relation.detailsJson.isConfidential} />,
                        })),
                        (entityId: number) =>
                            handleDeleteCoiCheckRelation(
                                coiCheckId,
                                connectedMatters.find((relation: CoiCheckRelation) => relation.entityId === entityId)
                                    ?.id
                            )
                    );

                    const clientLinks = getDeletableLinks(
                        'contact',
                        connectedClients.map((relation: CoiCheckRelation) => ({
                            id: relation.entityId,
                            name: relation.detailsJson.name ?? '',
                        })),
                        (entityId: number) =>
                            handleDeleteCoiCheckRelation(
                                coiCheckId,
                                connectedClients.find((relation: CoiCheckRelation) => relation.entityId === entityId)
                                    ?.id
                            ),
                        undefined,
                        matterLinks.length !== 0
                    );

                    const links = [...clientLinks, ...matterLinks];

                    return (
                        <React.Fragment>
                            {coiCheckId && (
                                <CoiCheckAddRelationButton
                                    connectedClients={connectedClients}
                                    connectedMatters={connectedMatters}
                                    coiCheckId={`${coiCheckId}`}
                                >
                                    <FormattedMessage id="coi.add-relations.connect-to" />
                                </CoiCheckAddRelationButton>
                            )}
                            {links.length > 0 && <div className="flex flex-wrap gap-1">{links}</div>}
                        </React.Fragment>
                    );
                },
            },
        ],
        [selectedCoiChecks, handleDeleteCoiCheckRelation, handleSearchTermClick, effectiveFormat]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'searchTerm' }],
        }),
        []
    );

    return (
        <ListTableWithFilters<CoiCheckListItem>
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="previous-coi-checks"
            columns={columns}
            data={data}
            isLoading={isFetching}
            fetchData={fetchData}
            fetchCount={useLazyGetCoiChecksCountQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
            pluralTextId="coi.records"
            singularTextId="coi.record"
            noEntriesId="coi.no-records-found"
            noEntriesBodyId="coi.empty.body"
            noEntriesIcon={FactCheckOutlinedIcon}
            renderSearchDescriptionLevelComponent={coiCheckActionButtons}
        />
    );
};

export default PreviousCoiChecksView;
